import { useRef } from 'react';
import { CardElement, useRecurly } from '@recurly/react-recurly';
import camelCase from 'lodash/camelCase';
import forEach from 'lodash/forEach';
import { func, number, shape, string } from 'prop-types';
import { track } from '@fff-web/fff-analytics';
import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared';
import useFormData from '../../hooks/useFormData';
import {
  getBillingInfo,
  invalidateUserBillingInfo,
  updateBillingInfo,
} from '../../services/api/billing';
import { AdminInput, AdminSelect } from '../../shared-components';
import Button, { BUTTON_TYPES } from '../../shared-components/Button/Button';
import { ACCOUNT_INFO, ACCOUNT_SECTIONS, ACCOUNT_TRACK_NAMES } from '../../utils/segment';
import * as styles from './Payment.module.css';

const BUTTON_CTAS = {
  save: 'SAVE',
  cancel: 'CANCEL',
};

const countries = [
  {
    name: 'United States',
    value: 'US',
  },
  {
    name: 'Canada',
    value: 'CA',
  },
  {
    name: 'United Kingdom',
    value: 'GB',
  },
];

const PaymentForm = ({ onClose, billingInfo, setBillingInfo }) => {
  const {
    formData,
    handleInputChange,
    handleErrors,
    formErrors,
    removeFormError,
    updatingForm,
    setUpdatingForm,
  } = useFormData(billingInfo);
  const formRef = useRef(null);
  const recurly = useRecurly();

  const getErrorMessage = (code, field) => {
    const displayMap = {
      postal_code: 'zip code',
      country: 'country',
      first_name: 'first name',
      last_name: 'last name',
    };
    if (code === 'validation') {
      return 'This is a required field.';
    }
    return `Please enter a valid ${displayMap[field]}.`;
  };
  const handleRecurlyError = (err) => {
    const { code, details, message } = err;
    if (message === 'Cvv must be three digits') {
      return handleErrors({
        creditCard: 'The CVV must be three digits for this type of credit card.',
      });
    }
    if (message === 'Cvv must be four digits') {
      return handleErrors({
        creditCard: 'The CVV must be four digits for this type of credit card.',
      });
    }
    return forEach(details, (d) => {
      if (d.field === 'number' || d.field === 'month' || d.field === 'year' || d.field === 'cvv') {
        handleErrors({
          creditCard: 'Please enter valid credit card information.',
        });
      }
      if (
        d.field === 'first_name' ||
        d.field === 'last_name' ||
        d.field === 'postal_code' ||
        d.field === 'country'
      ) {
        handleErrors({
          [camelCase(d.field)]: getErrorMessage(code, d.field),
        });
      }
    });
  };

  const handleSubmit = (event) => {
    if (event.preventDefault) event.preventDefault();
    setUpdatingForm(true);
    recurly.token(formRef.current, async (err, token) => {
      let updateResult = err ? 'failed' : 'success';
      if (err) {
        handleRecurlyError(err);
        showErrorToast(
          'Payment Update Failed',
          'Your payment information was not updated. Please try again.',
        );
      } else {
        try {
          await updateBillingInfo(token.id);
          // we need to make an additional API call to get billing info here
          // because the BE is unable to return updated credit card info
          const response = await getBillingInfo();
          setBillingInfo({ ...response, postalCode: response.zip });
          showSuccessToast(
            'Payment Updated',
            'Your payment information has been successfully updated.',
          );
          await invalidateUserBillingInfo();
          track({
            trackName: ACCOUNT_TRACK_NAMES.completed,
            properties: {
              cta_name: BUTTON_CTAS.save,
              account_section: ACCOUNT_SECTIONS.billing,
              info_updated: ACCOUNT_INFO.payment,
            },
          });
          onClose();
        } catch {
          showErrorToast(
            'Payment Update Failed',
            'Your payment information was not updated. Please try again.',
          );
          updateResult = 'payment failed';
        }
      }
      setUpdatingForm(false);
      track({
        trackName: 'CC Update: Update of payment method',
        properties: {
          update_result: updateResult,
        },
      });
    });
  };

  const handleRecurlyInput = (e) => {
    removeFormError('creditCard');
    if (!e.focus && !e.empty && !e.valid) {
      handleErrors({
        creditCard: 'Please enter valid credit card information.',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} ref={formRef} className={styles.formContainer}>
      <div className={styles.row}>
        <AdminInput
          type="text"
          dataRecurly="first_name"
          datatestid="first-name-payment-fld"
          label="First Name"
          error={formErrors.firstName}
          name="firstName"
          onChange={handleInputChange}
          value={formData.firstName || ''}
        />
        <AdminInput
          type="text"
          dataRecurly="last_name"
          datatestid="last-name-payment-fld"
          label="Last Name"
          error={formErrors.lastName}
          name="lastName"
          onChange={handleInputChange}
          value={formData.lastName || ''}
        />
      </div>
      <div className={styles.creditCard}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={styles.label}>Credit Card</label>
        <CardElement
          style={{
            placeholder: {
              content: 'Credit Card Number',
              fontFamily: '"Nunito Sans", sans-serif',
              fontSize: '14px',
              fontColor: 'black',
              opacity: '0.4',
            },
            invalid: {
              fontColor: '#cc484a',
            },
            fontFamily: '"Nunito Sans", sans-serif',
            fontSize: '14px',
          }}
          onChange={handleRecurlyInput}
        />
        {formErrors.creditCard && <span className={styles.error}>{formErrors.creditCard}</span>}
      </div>
      <div className={styles.row}>
        <AdminInput
          dataRecurly="postal_code"
          label="Zip Code"
          datatestid="zipcode-payment-fld"
          type="text"
          error={formErrors.postalCode}
          name="postalCode"
          onChange={handleInputChange}
          value={formData.postalCode || ''}
        />
        <AdminSelect
          dataRecurly="country"
          label="Country"
          error={formErrors.country}
          name="country"
          handleChange={handleInputChange}
          selected={formData.country || ''}
          list={countries}
          selectLabel="Select Country"
        />
      </div>
      <Button
        type="submit"
        buttonType={BUTTON_TYPES.PRIMARY}
        style={{ width: '132px' }}
        disabled={updatingForm}
      >
        {BUTTON_CTAS.save}
      </Button>
      <Button buttonType={BUTTON_TYPES.CANCEL} style={{ width: '132px' }} onClick={onClose}>
        {BUTTON_CTAS.cancel}
      </Button>
    </form>
  );
};

PaymentForm.propTypes = {
  onClose: func.isRequired,
  billingInfo: shape({
    cardType: string,
    country: string.isRequired,
    firstName: string.isRequired,
    firstSix: string,
    lastFour: string,
    lastName: string.isRequired,
    month: number,
    year: number,
    postalCode: string,
  }).isRequired,
  setBillingInfo: func.isRequired,
};

export default PaymentForm;
